import { useMemo } from "react";

import type { DeviceObservation, DeviceStatus } from "../api/types";

interface StatusSparklineProps {
  observations: DeviceObservation[];
  windowStart: string;
  windowEnd: string;
  /** Bucket count along the X axis. Default 144 = one 10-min bucket across 24h. */
  buckets?: number;
  /** Height of the bar strip in pixels. */
  height?: number;
}

const STATUS_COLOR: Record<DeviceStatus, string> = {
  alive: "var(--ok)",
  unreachable: "var(--crit)",
  "no-creds": "var(--warn)",
  error: "var(--crit)",
  unknown: "rgba(141, 150, 160, 0.45)",
};

// Severity order — when bucketing, the worst-non-empty status wins.
const STATUS_SEVERITY: Record<DeviceStatus, number> = {
  unknown: 0,
  alive: 1,
  "no-creds": 2,
  unreachable: 3,
  error: 4,
};

/** Downsample N observations into K buckets along the time axis, taking the
 *  highest-severity status seen within each bucket. Empty buckets render as
 *  faded "no data" segments. */
export function StatusSparkline({
  observations,
  windowStart,
  windowEnd,
  buckets = 144,
  height = 36,
}: StatusSparklineProps) {
  const { segments, totals } = useMemo(() => {
    const start = new Date(windowStart).getTime();
    const end = new Date(windowEnd).getTime();
    const span = Math.max(end - start, 1);
    const bucketStatuses: (DeviceStatus | null)[] = Array.from({ length: buckets }, () => null);

    for (const obs of observations) {
      const t = new Date(obs.observed_at).getTime();
      if (isNaN(t) || t < start || t > end) continue;
      const idx = Math.min(buckets - 1, Math.floor(((t - start) / span) * buckets));
      const prev = bucketStatuses[idx];
      if (!prev || STATUS_SEVERITY[obs.status] > STATUS_SEVERITY[prev]) {
        bucketStatuses[idx] = obs.status;
      }
    }

    const counts: Record<DeviceStatus | "no-data", number> = {
      alive: 0,
      unreachable: 0,
      "no-creds": 0,
      error: 0,
      unknown: 0,
      "no-data": 0,
    };
    for (const s of bucketStatuses) {
      if (s === null) counts["no-data"]++;
      else counts[s]++;
    }

    return { segments: bucketStatuses, totals: counts };
  }, [observations, windowStart, windowEnd, buckets]);

  const startLabel = new Date(windowStart).toLocaleString();
  const endLabel = new Date(windowEnd).toLocaleString();

  return (
    <div>
      <svg
        viewBox={`0 0 ${buckets} ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height, display: "block", borderRadius: 4 }}
        role="img"
        aria-label={`Status timeline from ${startLabel} to ${endLabel}, ${buckets} buckets.`}
      >
        {segments.map((status, i) => (
          <rect
            key={i}
            x={i}
            y={0}
            width={1}
            height={height}
            fill={status ? STATUS_COLOR[status] : "rgba(141, 150, 160, 0.12)"}
          />
        ))}
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.7rem",
          color: "var(--text-dim)",
          marginTop: 6,
        }}
      >
        <span>{startLabel}</span>
        <span>
          {observations.length} obs · {totals.alive} alive · {totals.unreachable} unreachable ·{" "}
          {totals["no-data"]} no-data
        </span>
        <span>{endLabel}</span>
      </div>
    </div>
  );
}
